/**
 * Resolves where hosted forms + the embed loader live, so links work in BOTH
 * local dev and production.
 *
 *   - Local dev: the Vite dev server proxies /f and /embed to the API, so we
 *     use the current origin (e.g. http://localhost:3001). Links resolve.
 *   - Production: forms are served from their own host. Override per-env with
 *     VITE_FORMS_HOST; otherwise we fall back to the canonical forms host.
 *
 * Keep PROD_FORMS_HOST in sync with DEFAULT_HOST in src/embed/loader.ts.
 */

export const PROD_FORMS_HOST = 'forms.engage.jobtetris.com';

function isLocalHost(host: string): boolean {
  return /^(localhost|127\.0\.0\.1)(:\d+)?$/.test(host);
}

/** { origin, host } for the forms surface. */
export function formsBase(): { origin: string; host: string } {
  const env = (import.meta.env.VITE_FORMS_HOST as string | undefined)?.trim();
  if (env) {
    const host = env.replace(/^https?:\/\//, '').replace(/\/$/, '');
    const proto = isLocalHost(host) ? 'http' : 'https';
    return { origin: `${proto}://${host}`, host };
  }
  // No override: locally use the current (proxied) origin; in prod use the
  // canonical forms host so the link points at where forms are actually served.
  if (typeof window !== 'undefined' && isLocalHost(window.location.host)) {
    return { origin: window.location.origin, host: window.location.host };
  }
  return { origin: `https://${PROD_FORMS_HOST}`, host: PROD_FORMS_HOST };
}

/** Public hosted URL for a published form slug. */
export function hostedFormUrl(slug: string): string {
  return `${formsBase().origin}/f/${slug}`;
}

/**
 * Copy-paste embed snippet for a form slug. Includes data-jt-host only when the
 * host differs from the production default (so local/staging embeds resolve,
 * while the production snippet stays clean and uses the loader's default host).
 */
export function embedSnippet(slug: string): string {
  const { origin, host } = formsBase();
  const needsHost = host !== PROD_FORMS_HOST;
  const div = `<div data-jt-form="${slug}"${needsHost ? ` data-jt-host="${host}"` : ''}></div>`;
  const script = `<script src="${origin}/embed/v1/loader.js" async></script>`;
  return `${div}\n${script}`;
}
